import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FaUsers,
  FaDownload,
  FaSync,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
  FaProjectDiagram,
  FaSearch,
  FaTimes,
  FaUserTie,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaMapMarkerAlt,
  FaMapMarkedAlt,
  FaChartBar,
  FaExclamationTriangle,
  FaFlagCheckered
} from 'react-icons/fa';
import { collection, getDocs, doc, query, where, getDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './SMOLeadReports.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useUserProfile } from '../contexts/UserProfileContext';
import ProjectVisitsModal from './ProfilePage/ProjectVisitsModal';
import { useTheme } from '../contexts/ThemeContext';
import { normalizeCity, normalizeManagerIds } from './CenterManagementPage/regionUtils';
import {
  isArchivedAssignment,
  getArchivedAssignmentLabel,
  formatUnassignedDate,
} from '../utils/assignmentHistory';
import { matchesSearchTerm } from '../utils/searchText';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line
} from 'recharts';

const BolgeMudurleriReports = () => {
  const { employeeId, userAuthorization } = useUserProfile();
  const { isDarkMode } = useTheme();

  // Region manager list state — one entry per region that has a manager
  const [managerViews, setManagerViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [loadingManagers, setLoadingManagers] = useState(true);

  // Workers under selected region
  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);

  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [primerFilter, setPrimerFilter] = useState('all');
  const [blindedFilter, setBlindedFilter] = useState('all');
  const [hideFinished, setHideFinished] = useState(false);

  // Table scroll state
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);

  // Visits modal state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  const [expandedWorkers, setExpandedWorkers] = useState({});

  // Visits for the selected region's workers
  const [allVisits, setAllVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);

  // Operational summary section state
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  // Collapsed cities state - tracks which cities are collapsed for each view
  const [collapsedCities, setCollapsedCities] = useState({});

  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');

  // ── Date range filter for the summary section ──────────────────────────
  const getMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const getToday = () => new Date().toISOString().split('T')[0];

  const [dateStart, setDateStart] = useState(getMonthStart);
  const [dateEnd, setDateEnd] = useState(getToday);

  const rangedVisits = useMemo(() => {
    return (allVisits || []).filter(v => {
      const d = v.date || v.firstVisitDate || '';
      if (!d) return false;
      if (dateStart && d < dateStart) return false;
      if (dateEnd && d > dateEnd) return false;
      return true;
    });
  }, [allVisits, dateStart, dateEnd]);

  // ── Operational summary (same metrics as SMO Lead reports) ─────────────
  const summary = useMemo(() => {
    const visits = rangedVisits;
    if (!visits || visits.length === 0) return null;

    const total = visits.length;
    const homeVisits = visits.filter(v => v.visitType === 'home');
    const siteVisits = visits.filter(v => v.visitType !== 'home');
    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;

    const issueCount = visits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
    const qualityRate = pct(total - issueCount, total);

    const withSLA = visits.filter(v =>
      v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate
    );
    const slaMet = withSLA.filter(v => {
      const cycle = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000);
      return cycle <= parseFloat(v.visitEvaluation.slaTarget);
    }).length;
    const slaRate = pct(slaMet, withSLA.length);
    const overdueCount = withSLA.length - slaMet;

    const ecrfDone = visits.filter(v => v.visitEvaluation?.ecrfCompletionDate).length;
    const sourceDone = visits.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length;
    const signDone = visits.filter(v => v.visitEvaluation?.signatureCompletionDate).length;
    const docRate = Math.round((pct(ecrfDone, total) + pct(sourceDone, total) + pct(signDone, total)) / 3);

    const missedCount = visits.filter(v => v.visitEvaluation?.issueResultedInMiss === 'yes').length;
    const deviationCount = visits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
    const deviationRate = pct(deviationCount, total);
    const aeSaeCount = visits.filter(v => v.visitEvaluation?.wasAeSaeTaken === 'yes').length;
    const missingProcessEnd = visits.filter(v => !v.visitEvaluation?.processEndDate).length;

    const totalKm = Math.round(visits.reduce((sum, v) =>
      sum + (parseFloat(v.visitEvaluation?.travelDistanceBefore) || 0), 0));

    const alerts = [];
    if (withSLA.length > 0 && slaRate < 80) alerts.push({ type: 'danger', text: `SLA karşılanma oranı %${slaRate} — hedefin altında` });
    if (pct(issueCount, total) > 10) alerts.push({ type: 'danger', text: `Sorun oranı %${pct(issueCount, total)} — yüksek seviyede` });
    if (deviationRate > 5) alerts.push({ type: 'warning', text: `Protokol sapma oranı %${deviationRate}` });
    if (missingProcessEnd > total * 0.2) alerts.push({ type: 'warning', text: `${missingProcessEnd} vizitte süreç bitiş tarihi eksik` });
    if (missedCount > 0) alerts.push({ type: 'danger', text: `${missedCount} vizit kaçırıldı` });
    if (aeSaeCount > 0) alerts.push({ type: 'info', text: `${aeSaeCount} AE/SAE rapor edildi` });

    // Monthly trend
    const monthlyData = {};
    visits.forEach(v => {
      const date = v.date || v.firstVisitDate || '';
      const month = date ? date.substring(0, 7) : null;
      if (!month) return;
      if (!monthlyData[month]) monthlyData[month] = { month, Ev: 0, Hastane: 0, Sorun: 0 };
      if (v.visitType === 'home') monthlyData[month].Ev++;
      else monthlyData[month].Hastane++;
      if (v.visitEvaluation?.issueOccurred === 'yes') monthlyData[month].Sorun++;
    });
    const monthlyTrend = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));

    // Per-worker visit distribution chart
    const perWorker = {};
    visits.forEach(v => {
      const w = workers.find(wk => wk.id === v.employeeId);
      const name = w ? `${w.name} ${w.surname}` : (v.employeeName || 'Bilinmeyen');
      if (!perWorker[name]) perWorker[name] = { name, Vizit: 0, Sorun: 0 };
      perWorker[name].Vizit++;
      if (v.visitEvaluation?.issueOccurred === 'yes') perWorker[name].Sorun++;
    });
    const workerChart = Object.values(perWorker)
      .sort((a, b) => b.Vizit - a.Vizit)
      .slice(0, 12)
      .map(w => ({ ...w, name: w.name.length > 16 ? w.name.substring(0, 15) + '…' : w.name, fullName: w.name }));

    return {
      total, homeCount: homeVisits.length, siteCount: siteVisits.length,
      qualityRate, slaRate, docRate, totalKm,
      issueCount, missedCount, deviationCount, aeSaeCount,
      overdueCount, missingProcessEnd,
      alerts, monthlyTrend, workerChart
    };
  }, [rangedVisits, workers]);

  // ── Build manager views from Regions + Centers + Employees ─────────────
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setLoadingManagers(true);

        const [regionsSnap, centersSnap] = await Promise.all([
          getDocs(collection(db, 'Regions')),
          getDocs(collection(db, 'Centers'))
        ]);

        const allCenters = centersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const regions = regionsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }));

        const viewSeeds = regions.flatMap(region => {
          const seeds = [];

          if (region.managerId) {
            seeds.push({
              viewKey: region.id,
              scope: 'region',
              region,
              managerId: region.managerId,
              cities: region.cities || []
            });
          }

          Object.entries(region.cityManagerIds || {}).forEach(([cityKey, managerIds]) => {
            const cityName = (region.cities || []).find(city => normalizeCity(city) === cityKey) || cityKey;
            normalizeManagerIds(managerIds).forEach(managerId => {
              seeds.push({
                viewKey: `${region.id}:${cityKey}:${managerId}`,
                scope: 'city',
                region,
                managerId,
                cityName,
                cities: [cityName]
              });
            });
          });

          return seeds;
        });

        // Fetch manager employee docs in parallel (unique ids)
        const managerIds = [...new Set(viewSeeds.map(seed => seed.managerId).filter(Boolean))];
        const managerDocs = await Promise.all(
          managerIds.map(id => getDoc(doc(db, 'Employees', id)).catch(() => null))
        );
        const managerDataMap = {};
        managerIds.forEach((id, idx) => {
          const d = managerDocs[idx];
          if (d && d.exists()) managerDataMap[id] = d.data();
        });

        const views = viewSeeds.map(seed => {
          const { region } = seed;
          const managerData = managerDataMap[seed.managerId];
          if (!managerData) return null;

          const normalizedCities = (seed.cities || []).map(normalizeCity);
          const regionCenters = allCenters
            .filter(c => normalizedCities.includes(normalizeCity(c['İl'])))
            .map(c => ({
              id: c.id,
              name: c['Adı'] || '',
              city: c['İl'] || '',
              district: c['İlçe'] || '',
              status: c['Durum'] || 'Aktif'
            }));

          return {
            viewKey: seed.viewKey,
            regionId: region.id,
            regionName: seed.scope === 'city'
              ? `${region.name || ''} / ${seed.cityName}`
              : region.name || '',
            baseRegionName: region.name || '',
            regionColor: region.color || '#3b82f6',
            cities: seed.cities || [],
            managerId: seed.managerId,
            scope: seed.scope,
            cityName: seed.cityName || '',
            name: managerData.eName || '',
            surname: managerData.eSurname || '',
            email: managerData.eMail || '',
            phone: managerData.phone || '',
            centers: regionCenters,
            centerCount: regionCenters.length
          };
        }).filter(Boolean);

        views.sort((a, b) => a.regionName.localeCompare(b.regionName, 'tr'));
        setManagerViews(views);
        setLoadingManagers(false);
      } catch (error) {
        console.error('Error fetching region managers:', error);
        toast.error('Bölge müdürleri yüklenirken hata oluştu!');
        setLoadingManagers(false);
      }
    };

    fetchManagers();
  }, []);

  // Auto-select own region for non-admin bölge müdürü users
  useEffect(() => {
    if (isAdmin || selectedView || !employeeId) return;
    const ownViews = managerViews.filter(v => v.managerId === employeeId);
    if (ownViews.length === 1) {
      setSelectedView(ownViews[0]);
    }
  }, [managerViews, isAdmin, employeeId, selectedView]);

  // ── Fetch workers when a region is selected ─────────────────────────────
  const fetchWorkersForRegion = useCallback(async () => {
    if (!selectedView) {
      setWorkers([]);
      return;
    }

    setLoadingWorkers(true);
    setExpandedWorkers({});

    try {
      const centerIds = selectedView.centers.map(c => c.id);

      const projectsSnapshot = await getDocs(collection(db, 'Projects'));

      // empId -> { emp, projects: [] }
      const empProjectMap = {};

      for (const projectDoc of projectsSnapshot.docs) {
        const projectData = projectDoc.data();
        const projectEmployees = projectData.employees || [];

        const centerEmployees = projectEmployees.filter(emp =>
          centerIds.includes(emp.centerId)
        );

        for (const emp of centerEmployees) {
          const isPrimer = emp.workerType === 'primer' || emp.workerType === 'Primer';
          const isBlinded = emp.blindStatus === 'blinded' || emp.blindStatus === 'Blinded';
          const center = selectedView.centers.find(c => c.id === emp.centerId);

          const projectInfo = {
            projectId: projectDoc.id,
            // String(): sayısal kaydedilmiş proje kodları arama/sıralamada
            // metin işlemlerine girdiği için tipi burada sabitliyoruz.
            projectTitle: String(projectData.titleShort || projectData.titleLong || ''),
            role: emp.role || '',
            centerId: emp.centerId,
            centerName: emp.centerName || center?.name || '',
            centerCity: center?.city || '',
            assignedAt: emp.assignedAt,
            status: emp.status || 'active',
            // Merkezden ayrılmış atamalar rapordan düşmez, rozetle işaretlenir.
            isArchived: isArchivedAssignment(emp),
            unassignedAt: emp.unassignedAt || '',
            isPrimer,
            isBlinded,
            isFinished: projectData.isFinished === true
          };

          if (empProjectMap[emp.id]) {
            empProjectMap[emp.id].projects.push(projectInfo);
          } else {
            empProjectMap[emp.id] = { emp, projects: [projectInfo] };
          }
        }
      }

      const uniqueEmpIds = Object.keys(empProjectMap);
      const empDocs = await Promise.all(
        uniqueEmpIds.map(id => getDoc(doc(db, 'Employees', id)).catch(() => null))
      );
      const empDataMap = {};
      uniqueEmpIds.forEach((id, idx) => {
        const empDoc = empDocs[idx];
        empDataMap[id] = empDoc && empDoc.exists() ? empDoc.data() : null;
      });

      const workersData = uniqueEmpIds.map(id => {
        const { emp, projects } = empProjectMap[id];
        const employeeData = empDataMap[id];
        return {
          id,
          name: emp.name || '',
          surname: emp.surname || '',
          email: employeeData?.eMail || '',
          phone: employeeData?.phone || '',
          projects
        };
      });

      workersData.sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));
      setWorkers(workersData);
      setLoadingWorkers(false);
    } catch (error) {
      console.error('Error fetching workers for region:', error);
      toast.error('Çalışan bilgileri yüklenirken hata oluştu!');
      setLoadingWorkers(false);
    }
  }, [selectedView]);

  useEffect(() => {
    fetchWorkersForRegion();
  }, [fetchWorkersForRegion]);

  // ── Fetch visits for region workers (batched 'in' queries) ─────────────
  useEffect(() => {
    if (workers.length === 0) { setAllVisits([]); return; }
    const fetchVisits = async () => {
      setLoadingVisits(true);
      try {
        const workerIds = workers.map(w => w.id);
        const BATCH_SIZE = 30;
        const batches = [];
        for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
          batches.push(workerIds.slice(i, i + BATCH_SIZE));
        }
        const results = await Promise.all(
          batches.map(batch => {
            const q = query(collection(db, 'Visits'), where('employeeId', 'in', batch));
            return getDocs(q);
          })
        );
        const visits = results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAllVisits(visits);
      } catch (e) {
        console.error('Error fetching visits for region dashboard:', e);
      } finally {
        setLoadingVisits(false);
      }
    };
    fetchVisits();
  }, [workers]);

  // Apply dark mode class
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // ── Table scroll handlers ───────────────────────────────────────────────
  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [workers]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      tableContainerRef.current.scrollTo({
        left: tableContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount),
        behavior: 'smooth'
      });
    }
  };

  const handleViewClick = (view) => {
    if (selectedView?.viewKey === view.viewKey) {
      setSelectedView(null);
    } else {
      setSelectedView(view);
    }
  };

  const toggleWorkerExpand = (workerId) => {
    setExpandedWorkers(prev => ({ ...prev, [workerId]: !prev[workerId] }));
  };

  const handleViewVisits = useCallback((worker, project) => {
    const workerVisits = allVisits.filter(
      v => v.employeeId === worker.id && v.projectId === project.projectId
    );

    if (workerVisits.length === 0) {
      toast.info('Bu çalışanın bu projedeki visit\'i bulunamadı.');
      return;
    }

    setSelectedEmployee({
      id: worker.id,
      name: `${worker.name} ${worker.surname}`,
      project: { id: project.projectId, title: project.projectTitle },
      visits: workerVisits
    });
    setShowVisitsModal(true);
  }, [allVisits]);

  // ── Filter workers ──────────────────────────────────────────────────────
  const filteredWorkers = useMemo(() => {
    return workers.map(worker => {
      let filteredProjects = worker.projects;

      if (statusFilter === 'active') {
        filteredProjects = filteredProjects.filter(p => p.status === 'active');
      } else if (statusFilter === 'inactive') {
        filteredProjects = filteredProjects.filter(p => p.status !== 'active');
      }

      if (primerFilter === 'primer') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === true);
      } else if (primerFilter === 'backup') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === false);
      }

      if (blindedFilter === 'blinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === true);
      } else if (blindedFilter === 'unblinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === false);
      }

      if (hideFinished) {
        filteredProjects = filteredProjects.filter(p => !p.isFinished);
      }

      if (searchTerm) {
        // Alanlar her zaman string değil (sayısal proje kodu, telefon vb.), bu
        // yüzden dönüşüm toSearchText üzerinden yapılır.
        const matchesWorker = matchesSearchTerm(
          searchTerm,
          `${worker.name} ${worker.surname}`,
          worker.email,
          worker.phone
        );

        if (!matchesWorker) {
          filteredProjects = filteredProjects.filter(p =>
            matchesSearchTerm(searchTerm, p.projectTitle, p.centerName, p.centerCity)
          );
        }
      }

      if (filteredProjects.length === 0) return null;
      return { ...worker, projects: filteredProjects };
    }).filter(Boolean);
  }, [workers, searchTerm, statusFilter, primerFilter, blindedFilter, hideFinished]);

  // ── Excel export ────────────────────────────────────────────────────────
  const downloadExcel = () => {
    const dataToExport = filteredWorkers.flatMap(worker =>
      worker.projects.map(project => ({
        'Bölge': selectedView?.regionName || '',
        'Bölge Müdürü': selectedView ? `${selectedView.name} ${selectedView.surname}` : '',
        'Çalışan Adı': `${worker.name} ${worker.surname}`,
        'E-posta': worker.email || '-',
        'Telefon': worker.phone || '-',
        'Merkez': project.centerName || '-',
        'Merkezden Ayrılma': project.isArchived ? formatUnassignedDate(project) : '',
        'İl': project.centerCity || '-',
        'Proje': project.projectTitle,
        'Rol': project.role,
        'Durum': project.status === 'active' ? 'Aktif' : 'Pasif',
        'Tip': project.isPrimer ? 'Primer' : 'Backup',
        'Kör Durumu': project.isBlinded ? 'Blinded' : 'Unblinded',
        'Atanma Tarihi': project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') ||
                        (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '-')
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');

    const regionName = selectedView ? selectedView.regionName.replace(/\s+/g, '_') : 'Tüm_Bölgeler';
    XLSX.writeFile(workbook, `${regionName}_Bölge_Çalışan_Listesi.xlsx`);
    toast.success('Excel dosyası başarıyla indirildi');
  };

  const downloadSummaryExcel = () => {
    if (!summary) return;
    const regionName = selectedView ? selectedView.regionName.replace(/\s+/g, '_') : 'Bölge';
    const data = [
      { 'Metrik': 'Toplam Vizit', 'Değer': summary.total },
      { 'Metrik': 'Ev Vizitleri', 'Değer': summary.homeCount },
      { 'Metrik': 'Hastane Vizitleri', 'Değer': summary.siteCount },
      { 'Metrik': 'Sorunsuz Vizit Oranı (%)', 'Değer': summary.qualityRate },
      { 'Metrik': 'SLA Karşılanma Oranı (%)', 'Değer': summary.slaRate },
      { 'Metrik': 'Dokümantasyon Tamlığı (%)', 'Değer': summary.docRate },
      { 'Metrik': 'Toplam KM', 'Değer': summary.totalKm },
      { 'Metrik': 'Sorunlu Vizit', 'Değer': summary.issueCount },
      { 'Metrik': 'Kaçırılan Vizit', 'Değer': summary.missedCount },
      { 'Metrik': 'Protokol Sapması', 'Değer': summary.deviationCount },
      { 'Metrik': 'AE/SAE', 'Değer': summary.aeSaeCount },
      { 'Metrik': 'Gecikmiş Vizit', 'Değer': summary.overdueCount },
      { 'Metrik': 'Süreç Bitiş Tarihi Eksik', 'Değer': summary.missingProcessEnd },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Operasyonel Özet');
    XLSX.writeFile(wb, `${regionName}_Operasyonel_Ozet.xlsx`);
    toast.success('Operasyonel Özet Excel indirildi');
  };

  // Views visible to the current user
  const visibleViews = useMemo(() => {
    if (isAdmin) return managerViews;
    return managerViews.filter(v => v.managerId === employeeId);
  }, [managerViews, isAdmin, employeeId]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        {/* Header */}
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.title}>
                <FaMapMarkedAlt className={styles.headerIcon} />
                Bölge Müdürleri Raporları
              </h1>
              <div className={styles.breadcrumb}>Raporlar / Bölge Müdürleri Raporları</div>
            </div>
            {selectedView && workers.length > 0 && (
              <div className={styles.headerButtons}>
                <button
                  className={`${styles.button} ${styles.refreshButton}`}
                  onClick={fetchWorkersForRegion}
                  disabled={loadingWorkers}
                  title="Çalışan verilerini yenile"
                >
                  <FaSync className={`${styles.icon} ${loadingWorkers ? styles.spinning : ''}`} />
                  {loadingWorkers ? 'Yenileniyor...' : 'Yenile'}
                </button>
                <button
                  className={`${styles.button} ${styles.downloadButton}`}
                  onClick={downloadExcel}
                >
                  <FaDownload className={styles.icon} /> Tabloyu İndir
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Stats Cards */}
        <div className={styles.cardContainer}>
          <div className={`${styles.card} ${styles.cardTotal}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTitle}>Toplam Bölge Sayısı</div>
              <div className={styles.cardValue}>{managerViews.length}</div>
            </div>
            <FaMapMarkedAlt className={styles.cardIcon} />
          </div>

          {selectedView && (
            <div className={`${styles.card} ${styles.cardActive}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Bölgedeki Merkez Sayısı</div>
                <div className={styles.cardValue}>{selectedView.centerCount}</div>
              </div>
              <FaBuilding className={styles.cardIcon} />
            </div>
          )}

          {selectedView && (
            <div className={`${styles.card} ${styles.cardInactive}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Bu Bölgede Çalışan Sayısı</div>
                <div className={styles.cardValue}>{workers.length}</div>
              </div>
              <FaUsers className={styles.cardIcon} />
            </div>
          )}
        </div>

        {/* Region Manager Selection Grid */}
        {isAdmin && (
          <div className={styles.smoSectionTitle}>
            <FaUserTie style={{ marginRight: '0.5rem' }} />
            Bölge Müdürleri
            <span className={styles.smoSectionSubtitle}>
              Detayları görmek için bir bölge seçiniz
            </span>
          </div>
        )}

        {loadingManagers ? (
          <div className={styles.loadingContainer}>
            <svg
              style={{ animation: 'spin 1s linear infinite', height: '2.5rem', width: '2.5rem', margin: '0 auto' }}
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.25 }}></circle>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" style={{ opacity: 0.75 }}></path>
            </svg>
            <p style={{ marginTop: '1rem' }}>Bölge müdürleri yükleniyor...</p>
          </div>
        ) : visibleViews.length === 0 ? (
          <div className={styles.emptyState}>
            <FaMapMarkedAlt className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>
              {isAdmin
                ? 'Henüz bölge müdürü atanmış bölge bulunmuyor. Merkez Yönetimi sayfasındaki "Bölge Yönetimi" ekranından bölge ve bölge müdürü tanımlayabilirsiniz.'
                : 'Size atanmış bir bölge bulunmuyor.'}
            </p>
          </div>
        ) : (!isAdmin && visibleViews.length === 1) ? null : (
          <div className={styles.smoGrid}>
            {visibleViews.map(view => (
              <div
                key={view.viewKey}
                className={`${styles.smoCard} ${selectedView?.viewKey === view.viewKey ? styles.smoCardSelected : ''}`}
                onClick={() => handleViewClick(view)}
                style={{ borderLeft: `5px solid ${view.regionColor}` }}
              >
                <div className={styles.smoCardAvatar} style={{ background: view.regionColor }}>
                  <FaUserTie />
                </div>
                <div className={styles.smoCardInfo}>
                  <div className={styles.smoCardName}>
                    {view.name} {view.surname}
                  </div>
                  <div className={styles.smoCardDetail} style={{ color: view.regionColor, fontWeight: 600 }}>
                    <FaMapMarkedAlt style={{ fontSize: '0.7rem' }} />
                    {view.regionName}
                  </div>
                  <div className={styles.smoCardDetail}>
                    <FaBuilding style={{ fontSize: '0.7rem' }} />
                    {view.centerCount} Merkez · {view.cities.length} İl
                  </div>
                  {view.email && (
                    <div className={styles.smoCardDetail}>{view.email}</div>
                  )}
                </div>
                {selectedView?.viewKey === view.viewKey && <div className={styles.smoCardCheck}>✓</div>}
              </div>
            ))}
          </div>
        )}

        {/* Workers Section */}
        {selectedView && (
          <div className={styles.workersSection}>
            <div className={styles.workersSectionHeader}>
              <h2 className={styles.workersSectionTitle}>
                <FaUsers style={{ marginRight: '0.5rem' }} />
                {selectedView.regionName} — {selectedView.name} {selectedView.surname} - Çalışanlar
              </h2>

              {/* City badges with collapsible centers */}
              <div className={styles.citySection}>
                {selectedView.cities
                  .filter(city => {
                    // 0 merkezli şehirleri gösterme
                    const centersInCity = selectedView.centers.filter(c => c.city === city);
                    return centersInCity.length > 0;
                  })
                  .map(city => {
                  const cityKey = `${selectedView.viewKey}-${city}`;
                  const centersInCity = selectedView.centers.filter(c => c.city === city);
                  const centerCount = centersInCity.length;
                  
                  // Tüm şehirler default kapalı gelsin
                  const isCollapsed = collapsedCities[cityKey] ?? true;
                  
                  // İlçelere göre grupla (10'dan fazla merkez varsa)
                  const groupByDistrict = centerCount > 10;
                  const districtGroups = {};
                  
                  if (groupByDistrict) {
                    centersInCity.forEach(center => {
                      const district = center.district || 'Bilinmeyen İlçe';
                      if (!districtGroups[district]) {
                        districtGroups[district] = [];
                      }
                      districtGroups[district].push(center);
                    });
                  }
                  
                  return (
                    <div key={city} className={styles.cityGroup}>
                      <div
                        className={styles.cityBadgeClickable}
                        style={{ borderColor: selectedView.regionColor }}
                        onClick={() => setCollapsedCities(prev => ({
                          ...prev,
                          [cityKey]: !(prev[cityKey] ?? true)
                        }))}
                      >
                        <FaMapMarkerAlt style={{ fontSize: '0.8rem', color: selectedView.regionColor }} />
                        <span className={styles.cityName}>{city}</span>
                        <span className={styles.centerCount}>
                          ({centerCount} Merkez{groupByDistrict ? ` · ${Object.keys(districtGroups).length} İlçe` : ''})
                        </span>
                        {isCollapsed ? (
                          <FaChevronDown className={styles.collapseIcon} />
                        ) : (
                          <FaChevronUp className={styles.collapseIcon} />
                        )}
                      </div>
                      
                      {!isCollapsed && (
                        <div className={styles.cityCenters}>
                          {groupByDistrict ? (
                            // İlçe bazlı gruplandırma
                            Object.entries(districtGroups)
                              .sort((a, b) => a[0].localeCompare(b[0], 'tr'))
                              .map(([district, centers]) => {
                                const districtKey = `${cityKey}-${district}`;
                                const isDistrictCollapsed = collapsedCities[districtKey] ?? true;
                                
                                return (
                                  <div key={district} className={styles.districtGroup}>
                                    <div
                                      className={styles.districtBadgeClickable}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCollapsedCities(prev => ({
                                          ...prev,
                                          [districtKey]: !(prev[districtKey] ?? true)
                                        }));
                                      }}
                                    >
                                      <span className={styles.districtName}>
                                        {district}
                                      </span>
                                      <span className={styles.districtCenterCount}>
                                        {centers.length} merkez
                                      </span>
                                      {isDistrictCollapsed ? (
                                        <FaChevronDown className={styles.districtCollapseIcon} />
                                      ) : (
                                        <FaChevronUp className={styles.districtCollapseIcon} />
                                      )}
                                    </div>
                                    
                                    {!isDistrictCollapsed && (
                                      <div className={styles.districtCenters}>
                                        {centers.map(center => (
                                          <span key={center.id} className={styles.centerBadge}>
                                            <FaBuilding style={{ fontSize: '0.65rem' }} />
                                            {center.name}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                          ) : (
                            // Direkt merkez listesi (10'dan az merkez)
                            centersInCity.map(center => (
                              <span key={center.id} className={styles.centerBadge}>
                                <FaBuilding style={{ fontSize: '0.65rem' }} />
                                {center.name}
                              </span>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Search and Filter */}
            {workers.length > 0 && (
              <div className={styles.searchContainer}>
                <div className={styles.searchBar}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Çalışan, proje, merkez veya il ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                  {searchTerm && (
                    <button className={styles.searchClear} onClick={() => setSearchTerm('')}>
                      <FaTimes />
                    </button>
                  )}
                </div>

                <div className={styles.modernFilterSection}>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Durum</label>
                    <select
                      className={styles.filterSelect}
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">Tümü ({workers.length})</option>
                      <option value="active">Aktif ({workers.filter(w => w.projects.some(p => p.status === 'active')).length})</option>
                      <option value="inactive">Pasif ({workers.filter(w => w.projects.some(p => p.status !== 'active')).length})</option>
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Tip</label>
                    <select
                      className={styles.filterSelect}
                      value={primerFilter}
                      onChange={(e) => setPrimerFilter(e.target.value)}
                    >
                      <option value="all">Tüm Tipler ({workers.length})</option>
                      <option value="primer">Primer ({workers.filter(w => w.projects.some(p => p.isPrimer === true)).length})</option>
                      <option value="backup">Backup ({workers.filter(w => w.projects.some(p => p.isPrimer === false)).length})</option>
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Kör Durumu</label>
                    <select
                      className={styles.filterSelect}
                      value={blindedFilter}
                      onChange={(e) => setBlindedFilter(e.target.value)}
                    >
                      <option value="all">Tümü ({workers.length})</option>
                      <option value="blinded">Blinded ({workers.filter(w => w.projects.some(p => p.isBlinded === true)).length})</option>
                      <option value="unblinded">Unblinded ({workers.filter(w => w.projects.some(p => p.isBlinded === false)).length})</option>
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Bitmiş Projeler</label>
                    <button
                      type="button"
                      className={`${styles.finishedToggle} ${hideFinished ? styles.finishedToggleActive : ''}`}
                      onClick={() => setHideFinished(v => !v)}
                      title="Bitmiş olarak işaretlenen projeleri listeden gizle"
                    >
                      <FaFlagCheckered />
                      {hideFinished ? 'Bitmiş projeler gizli' : 'Bitmiş projeleri gizle'}
                    </button>
                  </div>

                  {(statusFilter !== 'all' || primerFilter !== 'all' || blindedFilter !== 'all' || hideFinished) && (
                    <div className={styles.activeFiltersChips}>
                      {statusFilter !== 'all' && (
                        <span className={`${styles.filterChip} ${styles.filterChipStatus}`}>
                          {statusFilter === 'active' ? 'Aktif' : 'Pasif'}
                          <button onClick={() => setStatusFilter('all')} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      {primerFilter !== 'all' && (
                        <span className={`${styles.filterChip} ${styles.filterChipPrimer}`}>
                          {primerFilter === 'primer' ? 'Primer' : 'Backup'}
                          <button onClick={() => setPrimerFilter('all')} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      {blindedFilter !== 'all' && (
                        <span className={`${styles.filterChip} ${styles.filterChipBlinded}`}>
                          {blindedFilter === 'blinded' ? 'Blinded' : 'Unblinded'}
                          <button onClick={() => setBlindedFilter('all')} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      {hideFinished && (
                        <span className={`${styles.filterChip} ${styles.filterChipStatus}`}>
                          Bitmiş projeler gizli
                          <button onClick={() => setHideFinished(false)} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      <button
                        className={styles.clearAllFilters}
                        onClick={() => {
                          setStatusFilter('all');
                          setPrimerFilter('all');
                          setBlindedFilter('all');
                          setHideFinished(false);
                        }}
                      >
                        Tümünü Temizle
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {loadingWorkers ? (
              <div className={styles.loadingContainer}>
                <svg
                  style={{ animation: 'spin 1s linear infinite', height: '2rem', width: '2rem', margin: '0 auto' }}
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.25 }}></circle>
                  <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" style={{ opacity: 0.75 }}></path>
                </svg>
                <p style={{ marginTop: '0.75rem' }}>Çalışanlar yükleniyor...</p>
              </div>
            ) : filteredWorkers.length > 0 ? (
              <section className={styles.tableSection}>
                <div className={styles.tableWrapper}>
                  <div className={styles.tableScrollContainer}>
                    {showLeftScroll && (
                      <button className={`${styles.scrollButton} ${styles.scrollLeft}`} onClick={() => scrollTable('left')}>
                        <FaChevronLeft />
                      </button>
                    )}
                    {showRightScroll && (
                      <button className={`${styles.scrollButton} ${styles.scrollRight}`} onClick={() => scrollTable('right')}>
                        <FaChevronRight />
                      </button>
                    )}
                    <div ref={tableContainerRef} className={styles.tableContainer} onScroll={handleScroll}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Çalışan</th>
                            <th>E-posta</th>
                            <th>Telefon</th>
                            <th>Proje Sayısı</th>
                            <th>Merkez(ler)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredWorkers.map((worker) => (
                            <React.Fragment key={worker.id}>
                              <tr
                                className={`${styles.workerRow} ${expandedWorkers[worker.id] ? styles.workerRowExpanded : ''}`}
                                onClick={() => toggleWorkerExpand(worker.id)}
                              >
                                <td>
                                  <span className={styles.expandIcon}>
                                    {expandedWorkers[worker.id] ? <FaChevronUp /> : <FaChevronDown />}
                                  </span>
                                </td>
                                <td className={styles.workerName}>{worker.name} {worker.surname}</td>
                                <td>{worker.email || '-'}</td>
                                <td>{worker.phone || '-'}</td>
                                <td>
                                  <span className={styles.projectCountBadge}>
                                    {worker.projects.length}
                                  </span>
                                </td>
                                <td>
                                  {[...new Set(worker.projects.map(p => p.centerName))].join(', ') || '-'}
                                </td>
                              </tr>

                              {expandedWorkers[worker.id] && (
                                <tr className={styles.expandedRow}>
                                  <td colSpan={6}>
                                    <div className={styles.expandedContent}>
                                      <div className={styles.projectsGrid}>
                                        {worker.projects.map((project, idx) => (
                                          <div key={`${project.projectId}-${idx}`} className={`${styles.projectCard} ${project.isFinished ? styles.projectCardFinished : ''}`}>
                                            {project.isFinished && (
                                              <span className={styles.finishedRibbon}>
                                                <FaFlagCheckered style={{ marginRight: 4 }} />
                                                Bitti
                                              </span>
                                            )}
                                            <div className={styles.projectCardHeader}>
                                              <FaProjectDiagram className={styles.projectCardIcon} />
                                              <span className={styles.projectCardTitle}>{project.projectTitle}</span>
                                              <span className={`${styles.statusBadge} ${project.status === 'active' ? styles.statusActive : styles.statusInactive}`}>
                                                {project.status === 'active' ? 'Aktif' : 'Pasif'}
                                              </span>
                                            </div>
                                            <div className={styles.projectCardDetails}>
                                              <div className={styles.projectCardDetail}>
                                                <span className={styles.detailLabel}>Rol:</span>
                                                <span className={styles.detailValue}>{project.role || '-'}</span>
                                              </div>
                                              <div className={styles.projectCardDetail}>
                                                <span className={styles.detailLabel}>Merkez:</span>
                                                <span className={styles.detailValue}>
                                                  {project.centerName || '-'}
                                                  {project.isArchived && (
                                                    <span className={styles.archivedBadge}>
                                                      {getArchivedAssignmentLabel(project)}
                                                    </span>
                                                  )}
                                                </span>
                                              </div>
                                              <div className={styles.projectCardDetail}>
                                                <span className={styles.detailLabel}>İl:</span>
                                                <span className={styles.detailValue}>{project.centerCity || '-'}</span>
                                              </div>
                                              <div className={styles.projectCardDetail}>
                                                <span className={styles.detailLabel}>Tip:</span>
                                                <span className={styles.detailValue}>{project.isPrimer ? 'Primer' : 'Backup'}</span>
                                              </div>
                                              <div className={styles.projectCardDetail}>
                                                <span className={styles.detailLabel}>Kör:</span>
                                                <span className={styles.detailValue}>{project.isBlinded ? 'Blinded' : 'Unblinded'}</span>
                                              </div>
                                              <div className={styles.projectCardDetail}>
                                                <span className={styles.detailLabel}>Atanma Tarihi:</span>
                                                <span className={styles.detailValue}>
                                                  {project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') ||
                                                   (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '-')}
                                                </span>
                                              </div>
                                            </div>
                                            <button
                                              className={styles.viewVisitsButton}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewVisits(worker, project);
                                              }}
                                            >
                                              <FaEye style={{ marginRight: '0.375rem' }} />
                                              Visitleri Görüntüle
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              <div className={styles.emptyState}>
                <FaUsers className={styles.emptyStateIcon} />
                <p className={styles.emptyStateText}>
                  {searchTerm ? 'Arama kriterine uygun çalışan bulunamadı.' : 'Bu bölgede çalışan bulunmamaktadır.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Operasyonel Genel Bakış */}
        {selectedView && (
          <div className={styles.capaDashboardSection}>
            <div className={styles.smoSectionTitle}>
              <FaChartBar style={{ marginRight: '0.5rem' }} />
              Operasyonel Genel Bakış
              {loadingVisits && <span className={styles.capaDashboardSubtitle}>Yükleniyor...</span>}
              {!loadingVisits && summary && (
                <span className={styles.capaDashboardSubtitle}>
                  {summary.total} vizit analiz edildi ({summary.homeCount} ev / {summary.siteCount} hastane)
                </span>
              )}
              {!loadingVisits && summary && (
                <button className={styles.sectionDownloadButton} onClick={downloadSummaryExcel} title="Excel olarak indir">
                  <FaDownload /> Excel
                </button>
              )}
              <button
                className={styles.sectionToggleButton}
                onClick={() => setSummaryExpanded(prev => !prev)}
                title={summaryExpanded ? 'Daralt' : 'Genişlet'}
              >
                {summaryExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>

            <div className={styles.dateFilterRow}>
              <label className={styles.dateFilterLabel}>Tarih Aralığı:</label>
              <input type="date" className={styles.dateFilterInput} value={dateStart} onChange={e => setDateStart(e.target.value)} />
              <span className={styles.dateFilterSep}>—</span>
              <input type="date" className={styles.dateFilterInput} value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
              <button className={styles.dateFilterBtn} onClick={() => { setDateStart(getMonthStart()); setDateEnd(getToday()); }} title="Bu ay">Bu Ay</button>
              <button className={styles.dateFilterBtn} onClick={() => { setDateStart(''); setDateEnd(''); }} title="Tüm zamanlar">Tümü</button>
            </div>

            {!loadingVisits && !summary && (
              <div className={styles.emptyState}>
                <FaExclamationTriangle className={styles.emptyStateIcon} style={{ color: '#a0aec0' }} />
                <p className={styles.emptyStateText}>Seçilen tarih aralığında vizit bulunamadı.</p>
              </div>
            )}

            {!loadingVisits && summary && summaryExpanded && (
              <>
                {/* Hero Metric Cards */}
                <div className={styles.storyHeroCards}>
                  <div className={`${styles.storyHeroCard} ${styles.storyHeroBlue}`}>
                    <div className={styles.storyHeroValue}>{summary.total}</div>
                    <div className={styles.storyHeroLabel}>Toplam Vizit</div>
                    <div className={styles.storyHeroSub}>{summary.homeCount} ev · {summary.siteCount} hastane</div>
                  </div>
                  <div className={`${styles.storyHeroCard} ${summary.qualityRate >= 90 ? styles.storyHeroGreen : summary.qualityRate >= 70 ? styles.storyHeroYellow : styles.storyHeroRed}`}>
                    <div className={styles.storyHeroValue}>%{summary.qualityRate}</div>
                    <div className={styles.storyHeroLabel}>Sorunsuz Vizit Oranı</div>
                    <div className={styles.storyHeroSub}>{summary.issueCount} sorunlu vizit</div>
                  </div>
                  <div className={`${styles.storyHeroCard} ${summary.slaRate >= 80 ? styles.storyHeroGreen : summary.slaRate >= 50 ? styles.storyHeroYellow : styles.storyHeroRed}`}>
                    <div className={styles.storyHeroValue}>%{summary.slaRate}</div>
                    <div className={styles.storyHeroLabel}>SLA Karşılanma</div>
                    <div className={styles.storyHeroSub}>{summary.overdueCount} gecikmiş vizit</div>
                  </div>
                  <div className={`${styles.storyHeroCard} ${summary.docRate >= 80 ? styles.storyHeroGreen : summary.docRate >= 50 ? styles.storyHeroYellow : styles.storyHeroRed}`}>
                    <div className={styles.storyHeroValue}>%{summary.docRate}</div>
                    <div className={styles.storyHeroLabel}>Dokümantasyon Tamlığı</div>
                    <div className={styles.storyHeroSub}>eCRF + Kaynak + İmza ortalaması</div>
                  </div>
                  <div className={`${styles.storyHeroCard} ${styles.storyHeroPurple}`}>
                    <div className={styles.storyHeroValue}>{summary.totalKm > 0 ? summary.totalKm.toLocaleString('tr-TR') : '—'}</div>
                    <div className={styles.storyHeroLabel}>Toplam KM</div>
                    <div className={styles.storyHeroSub}>Seyahat edilen mesafe</div>
                  </div>
                </div>

                {/* Alerts */}
                {summary.alerts.length > 0 && (
                  <div className={styles.storyAlerts}>
                    {summary.alerts.map((alert, i) => (
                      <div
                        key={i}
                        className={`${styles.storyAlert} ${alert.type === 'danger' ? styles.storyAlertDanger : alert.type === 'warning' ? styles.storyAlertWarning : styles.storyAlertInfo}`}
                      >
                        <FaExclamationTriangle />
                        <span>{alert.text}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Charts */}
                <div className={styles.capaChartGrid}>
                  {summary.monthlyTrend.length > 1 && (
                    <div className={styles.capaChartCard}>
                      <h4 className={styles.capaChartTitle}>Aylık Vizit Trendi</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={summary.monthlyTrend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="Ev" stroke="#4299e1" strokeWidth={2} />
                          <Line type="monotone" dataKey="Hastane" stroke="#9f7aea" strokeWidth={2} />
                          <Line type="monotone" dataKey="Sorun" stroke="#e53e3e" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {summary.workerChart.length > 0 && (
                    <div className={styles.capaChartCard}>
                      <h4 className={styles.capaChartTitle}>Çalışan Bazında Vizit Dağılımı</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={summary.workerChart}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={70} />
                          <YAxis allowDecimals={false} />
                          <Tooltip labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label} />
                          <Legend />
                          <Bar dataKey="Vizit" fill={selectedView.regionColor} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Sorun" fill="#e53e3e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Visits Modal */}
      {showVisitsModal && selectedEmployee && (
        <ProjectVisitsModal
          show={showVisitsModal}
          onHide={() => {
            setShowVisitsModal(false);
            setSelectedEmployee(null);
          }}
          project={selectedEmployee.project}
          employeeId={selectedEmployee.id}
          employeeName={selectedEmployee.name}
          visits={selectedEmployee.visits}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </div>
  );
};

export default BolgeMudurleriReports;
